import { lengthLabel, type LengthUnit } from "@CommonUtils/cdtStudio/domain/studioTypes";
// Measure on itemsWithLayout — segment xCm is an anchor. Check 9 needs the same spine.
import { auditContiguity } from "../contiguityAudit";
import { itemsWithLayout, slabDepthOf } from "../verticalBlockingFix";
import { rowCapacityOf, rowWindowsOf } from "@CommonUtils/cdtStudio/domain/wallRows";
import {
  GeneratedLayout,
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
} from "../studioTypes";
import { declaredOrderViolations, laneOrderFindings } from "./blockOrderAudit";
import { fillByRunOf, fillShareOf } from "../cdtLayout/closeFillGap";
import { diagnoseColumns } from "./columnDiagnosis";
import { runBayWidthOf, slacksOf } from "../bayShare";
import { categoryLengthUnitOf } from "../studioLengthUnit";

export interface MoveAuditFailure {
  check: number;
  detail: string;
}

export interface MoveAudit {
  pass: boolean;
  failures: readonly MoveAuditFailure[];
  measured: {
    strandedBefore: number;
    strandedAfter: number;
    ceilingBefore: number;
    ceilingAfter: number;
    newBreaks: number;
    roundTripRate: { exact: number; total: number } | null;
    /** Fill percent per side - hard-gated only when the caller supplies `fill`. */
    fillBefore: number;
    fillAfter: number;
  };
}

export interface MoveAuditInputs {
  items: readonly StudioItem[];
  /** The category's unit, for the findings' words only; absent, `categoryLengthUnitOf`'s
   *  default. */
  lengthUnit?: LengthUnit;
  runs: readonly StudioRunGeometry[];
  before: { layout: GeneratedLayout; draft: StudioDraft };
  after: { layout: GeneratedLayout; draft: StudioDraft };
  /** Checks 1–2. Omitted for level moves, which declare no value order. */
  declared?: {
    previous: readonly string[];
    next: readonly string[];
    travelling: readonly string[];
    blockKeyOf: (item: StudioItem) => string;
    /** Lane-order rank over EVERY block the keying can produce; defaults to ranking `next`. */
    rank?: ReadonlyMap<string, number>;
    /** Per-run rank overrides; win over the maps when provided. */
    rankOf?: (run: number, blockKey: string) => number;
    /** The SAME rank BEFORE the move - check 2 is baseline-relative, so only NEW disorder fails. */
    previousRank?: ReadonlyMap<string, number>;
    previousRankOf?: (run: number, blockKey: string) => number;
    /** Per-run rank for blocks OUTSIDE the moved scope. Same rank both sides, so only a NEW
     * inter-se change fails check 2. */
    outsideRankOf?: (run: number, blockKey: string) => number | undefined;
  };
  /** Check 11: the committed draft's regeneration vs the transformed layout. */
  roundTrip?: { reproduced: GeneratedLayout; floorExact: number };
  /** Check 13 - fill: keep at least `floorShare` of shelf capacity and lose no more than
   * `dropTolerancePts` points against the before plan. */
  fill?: { floorShare: number; dropTolerancePts: number };
}

const placedSkuIds = (layout: GeneratedLayout): Set<string> => {
  const placed = new Set<string>();
  for (const placement of Object.values(layout.placements)) {
    if (placement.segments.some((segment) => segment.hFacings >= 1)) placed.add(placement.skuId);
  }
  return placed;
};

/** Shelf-exact agreement between two layouts, by each SKU's dominant segment. THE one ruler:
 * `buildLayout.test.ts` imports it too, so check 11 and the benchmark are like-with-like. */
export const shelfExactRate = (
  reference: GeneratedLayout,
  candidate: GeneratedLayout,
): { exact: number; total: number } => {
  let exact = 0;
  let total = 0;
  for (const placement of Object.values(reference.placements)) {
    if (placement.segments.length === 0) continue;
    total += 1;
    const other = candidate.placements[placement.skuId];
    if (!other || other.segments.length === 0) continue;
    const dominant = placement.segments.reduce((a, b) => (a.hFacings >= b.hFacings ? a : b));
    const otherDominant = other.segments.reduce((a, b) => (a.hFacings >= b.hFacings ? a : b));
    if (dominant.run === otherDominant.run && dominant.shelf === otherDominant.shelf) exact += 1;
  }
  return { exact, total };
};

export const auditMove = (inputs: MoveAuditInputs): MoveAudit => {
  const { items, runs, before, after, declared, roundTrip } = inputs;
  const lengthUnit = inputs.lengthUnit ?? categoryLengthUnitOf();
  const failures: MoveAuditFailure[] = [];

  // 1–2 · the CDT order contract, by construction and on every lane.
  if (declared) {
    for (const detail of declaredOrderViolations(
      declared.previous,
      declared.next,
      declared.travelling,
    )) {
      failures.push({ check: 1, detail });
    }
    const rank = declared.rank ?? new Map(declared.next.map((key, index) => [key, index]));
    const afterFindings = laneOrderFindings(after.layout, items, {
      rank,
      rankOf: declared.rankOf,
      blockKeyOf: declared.blockKeyOf,
    });
    // The exemption key is DELIBERATELY block-scoped (kind:blockKey, no laneKey): a block already
    // carrying disorder re-flows onto whichever lanes the re-deal picks.
    const baseline = new Set(
      laneOrderFindings(before.layout, items, {
        rank: declared.previousRank ?? new Map(declared.previous.map((key, index) => [key, index])),
        rankOf: declared.previousRankOf,
        blockKeyOf: declared.blockKeyOf,
      }).map((finding) => `${finding.kind}:${finding.blockKey}`),
    );
    for (const finding of afterFindings) {
      if (baseline.has(`${finding.kind}:${finding.blockKey}`)) continue;
      failures.push({
        check: 2,
        detail:
          finding.kind === "interleaved"
            ? `lane ${finding.laneKey} newly interleaves "${finding.blockKey}"`
            : `lane ${finding.laneKey} newly reads "${finding.blockKey}" out of declared order`,
      });
    }
    // Outside blocks, inter se: same rank on both sides, so only NEW findings fail.
    if (declared.outsideRankOf) {
      const outside = declared.outsideRankOf;
      const outsideOptions = {
        rank: new Map<string, number>(),
        rankOf: (run: number, key: string) => outside(run, key) ?? Number.MAX_SAFE_INTEGER,
        blockKeyOf: declared.blockKeyOf,
      };
      const outsideBaseline = new Set(
        laneOrderFindings(before.layout, items, outsideOptions).map(
          (finding) => `${finding.kind}:${finding.blockKey}`,
        ),
      );
      for (const finding of laneOrderFindings(after.layout, items, outsideOptions)) {
        if (outsideBaseline.has(`${finding.kind}:${finding.blockKey}`)) continue;
        failures.push({
          check: 2,
          detail:
            finding.kind === "interleaved"
              ? `lane ${finding.laneKey} newly interleaves outside block "${finding.blockKey}"`
              : `lane ${finding.laneKey} newly reorders outside block "${finding.blockKey}"`,
        });
      }
    }
  }

  // 3 · assortment unchanged, both directions.
  const beforeSkus = placedSkuIds(before.layout);
  const afterSkus = placedSkuIds(after.layout);
  const gained = [...afterSkus].filter((skuId) => !beforeSkus.has(skuId));
  const lost = [...beforeSkus].filter((skuId) => !afterSkus.has(skuId));
  if (gained.length > 0) {
    failures.push({ check: 3, detail: `assortment grew by ${gained.length} (${gained[0]}…)` });
  }
  if (lost.length > 0) {
    failures.push({ check: 3, detail: `assortment shrank by ${lost.length} (${lost[0]}…)` });
  }

  // 4–5 · physical: lane fill within the run, no overlapping stretches.
  const runById = new Map(runs.map((run) => [run.run, run]));
  const windowsByRun = new Map(runs.map((run) => [run.run, rowWindowsOf(run)]));
  const lanes = new Map<string, { xCm: number; widthCm: number }[]>();
  for (const item of itemsWithLayout(items, after.layout)) {
    for (const placement of item.placements) {
      const laneKey = `${placement.run}|${placement.shelf}`;
      const lane = lanes.get(laneKey) ?? [];
      lane.push({ xCm: placement.xCm, widthCm: placement.widthCm });
      lanes.set(laneKey, lane);
    }
  }
  for (const [laneKey, lane] of lanes) {
    const [runId, rank] = laneKey.split("|").map(Number);
    const run = runById.get(runId);
    const cap = run ? rowCapacityOf(run, rank) : undefined;
    // Fill tolerance, the prototype harness's own (acceptance.mjs), as a share of the run's bay.
    const slack = slacksOf(runBayWidthOf(run, runs));
    const fill = lane.reduce((sum, entry) => sum + entry.widthCm, 0);
    if (cap !== undefined && fill > cap + slack.fill) {
      failures.push({
        check: 4,
        detail: `lane ${laneKey} holds ${lengthLabel(fill, lengthUnit)} of a ${lengthLabel(cap, lengthUnit)} run`,
      });
    }
    const windows = (windowsByRun.get(runId) ?? []).filter((window) => window.rank === rank);
    const unboarded = lane.find(
      (entry) =>
        !windows.some(
          (window) => entry.xCm >= window.startCm - slack.fill && entry.xCm < window.endCm,
        ),
    );
    if (run && unboarded) {
      failures.push({
        check: 4,
        detail: `lane ${laneKey} has a pack at ${lengthLabel(unboarded.xCm, lengthUnit)} with no board under it`,
      });
    }
    const sorted = [...lane].sort((a, b) => a.xCm - b.xCm);
    for (let index = 1; index < sorted.length; index++) {
      const prev = sorted[index - 1];
      if (sorted[index].xCm < prev.xCm + prev.widthCm - slack.fit) {
        failures.push({ check: 5, detail: `lane ${laneKey} has overlapping stretches` });
        break;
      }
    }
  }

  // 6 · packs too tall for every opening, as reported by the generator; must not grow.
  const ceilingBefore = before.layout.ceilingViolations?.length ?? 0;
  const ceilingAfter = after.layout.ceilingViolations?.length ?? 0;
  if (ceilingAfter > ceilingBefore) {
    failures.push({ check: 6, detail: `ceiling violations grew ${ceilingBefore}→${ceilingAfter}` });
  }

  // 7 · every stretch keeps at least one facing.
  for (const placement of Object.values(after.layout.placements)) {
    if (placement.segments.some((segment) => segment.hFacings < 1)) {
      failures.push({ check: 7, detail: `${placement.skuId} has a stretch below one facing` });
    }
  }

  // 8 · stranded columns must not grow beyond the harness's own allowance (ref + 2): the greedy
  // census re-groups by x-adjacency, so a pure reorder can shift the count by a column or two.
  const strandedBefore = diagnoseColumns(before.layout, items, runs, before.draft).partial;
  const strandedAfter = diagnoseColumns(after.layout, items, runs, after.draft).partial;
  if (strandedAfter > strandedBefore + 2) {
    failures.push({ check: 8, detail: `stranded columns grew ${strandedBefore}→${strandedAfter}` });
  }

  // 9 · child bands keep their shelf spread and stay disjoint - only while both sides share a spine.
  const sameSpine =
    before.draft.spine.map((level) => level.attr).join("|") ===
    after.draft.spine.map((level) => level.attr).join("|");
  if (sameSpine) {
    const bandShelves = (layout: GeneratedLayout, draft: StudioDraft): Map<string, Set<number>> => {
      const depth = Math.min(slabDepthOf(draft) + 1, draft.spine.length);
      const bySku = new Map(items.map((item) => [item.skuId, item]));
      const spread = new Map<string, Set<number>>();
      for (const placement of Object.values(layout.placements)) {
        const item = bySku.get(placement.skuId);
        if (!item) continue;
        const attrs = effectiveAttrs(item, draft);
        const key = draft.spine
          .slice(0, depth)
          .map((level) => attrs[level.attr] ?? "")
          .join(PATH_SEPARATOR);
        const shelves = spread.get(key) ?? new Set<number>();
        for (const segment of placement.segments) shelves.add(segment.shelf);
        spread.set(key, shelves);
      }
      return spread;
    };
    const spreadBefore = bandShelves(before.layout, before.draft);
    const spreadAfter = bandShelves(after.layout, after.draft);
    for (const [key, was] of spreadBefore) {
      if (was.size < 2) continue;
      const now = spreadAfter.get(key);
      if (!now || now.size < 2) {
        failures.push({
          check: 9,
          detail: `band "${key}" collapsed ${was.size}→${now?.size ?? 0} shelves`,
        });
        break;
      }
    }
    // Disjoint sibling bands (same slab-prefix parent) must stay disjoint.
    const parentOf = (key: string) => key.split(PATH_SEPARATOR).slice(0, -1).join(PATH_SEPARATOR);
    const keys = [...spreadBefore.keys()];
    outer: for (let i = 0; i < keys.length; i++) {
      for (let j = i + 1; j < keys.length; j++) {
        if (parentOf(keys[i]) !== parentOf(keys[j])) continue;
        const wasDisjoint = ![...(spreadBefore.get(keys[i]) ?? [])].some((shelf) =>
          spreadBefore.get(keys[j])?.has(shelf),
        );
        if (!wasDisjoint) continue;
        const overlapsNow = [...(spreadAfter.get(keys[i]) ?? [])].some((shelf) =>
          spreadAfter.get(keys[j])?.has(shelf),
        );
        if (overlapsNow) {
          failures.push({
            check: 9,
            detail: `bands "${keys[i]}" and "${keys[j]}" were disjoint and now share a shelf`,
          });
          break outer;
        }
      }
    }
  }

  // 10 · no new contiguity breaks, under the after-draft's block identity.
  const blockDepth = Math.min(2, after.draft.spine.length);
  const blockOf = (item: StudioItem): string => {
    const attrs = effectiveAttrs(item, after.draft);
    return after.draft.spine
      .slice(0, blockDepth)
      .map((level) => attrs[level.attr] ?? "")
      .join(PATH_SEPARATOR);
  };
  const provenance = auditContiguity(before.layout, after.layout, items, blockOf, runs);
  if (provenance.newBreaks > 0) {
    failures.push({
      check: 10,
      detail: `${provenance.newBreaks} new contiguity break(s): ${provenance.broken
        .filter((entry) => entry.introduced)
        .map((entry) => entry.node)
        .join(", ")}`,
    });
  }

  // 11 · the committed draft reproduces the transformed layout.
  let roundTripRate: { exact: number; total: number } | null = null;
  if (roundTrip) {
    roundTripRate = shelfExactRate(after.layout, roundTrip.reproduced);
    if (roundTripRate.exact < roundTrip.floorExact) {
      failures.push({
        check: 11,
        detail:
          "regenerating the committed draft reproduces only " +
          `${roundTripRate.exact}/${roundTripRate.total} shelf-exact ` +
          `(floor ${roundTrip.floorExact})`,
      });
    }
  }

  // 13 · fill, gated PER RUN: an aisle at 83% must never hide behind a healthy average. The basis
  // caps each lane at the run width, so overlap can never masquerade as fill.
  const fillBefore = +(100 * fillShareOf(before.layout, items, runs)).toFixed(1);
  const fillAfter = +(100 * fillShareOf(after.layout, items, runs)).toFixed(1);
  if (inputs.fill) {
    const floorPct = +(100 * inputs.fill.floorShare).toFixed(1);
    const beforeByRun = new Map(
      fillByRunOf(before.layout, items, runs).map((entry) => [
        entry.run,
        +(100 * entry.share).toFixed(1),
      ]),
    );
    for (const entry of fillByRunOf(after.layout, items, runs)) {
      const pct = +(100 * entry.share).toFixed(1);
      // A move answers only for what it changed: a plan already under the floor is the plan's debt.
      const beforePct = beforeByRun.get(entry.run) ?? 0;
      if (pct < floorPct && pct < beforePct - inputs.fill.dropTolerancePts) {
        failures.push({
          check: 13,
          detail:
            `run ${entry.run} fill ${pct}% is below the spec band's floor (${floorPct}%) ` +
            `and below its own before-state (${beforePct}%)`,
        });
      }
    }
    if (fillAfter < fillBefore - inputs.fill.dropTolerancePts) {
      failures.push({
        check: 13,
        detail: `fill fell ${fillBefore}% → ${fillAfter}% (tolerance ${inputs.fill.dropTolerancePts}pt)`,
      });
    }
  }

  return {
    pass: failures.length === 0,
    failures,
    measured: {
      strandedBefore,
      strandedAfter,
      ceilingBefore,
      ceilingAfter,
      newBreaks: provenance.newBreaks,
      roundTripRate,
      fillBefore,
      fillAfter,
    },
  };
};
